import React, { useState } from "react"
import CustomButton from "../OnboardingComponents/CustomButton"
import CustomIconButton from "../OnboardingComponents/CustomIconButton"
import EmptyState from "../Common/EmptyState"
import TruncatedText from "../Common/TruncatedText"
import DeleteConfirmation from "../Common/DeleteConfirmation"
import Pagination from "../Listing/Pagination"
import SearchInput from "../Listing/SearchInput"
import FilterDropdown from "../Listing/FilterDropdown"
import { Avatar } from "./AvatarGroup"
import SessionDetailMeta from "./sessionDetail/SessionDetailMeta"
import SessionTimerControl from "./sessionDetail/SessionTimerControl"
import AddSessionCollaboratorModal from "../Modals/AddSessionCollaboratorModal"
import { ICONS } from "../../constants/icons"
import {
  DEFAULT_SORT_BY,
  DEFAULT_SORT_DIRECTION,
} from "../../helpers/sessionDetail"
import { isSameUserId, nameWithYou } from "../../helpers/userDisplay"

const STATUS_CLASS = {
  "In Session": "in-session",
  Joined: "in-session",
  Paused: "paused",
  Invited: "invited",
  Exited: "exited",
  Left: "exited",
}

const SESSION_COLLAB_STATUS_OPTIONS = [
  { value: "invited", label: "Invited" },
  { value: "joined", label: "Joined" },
  { value: "left", label: "Left" },
]

const SESSION_COLLAB_SORT_OPTIONS = [
  { value: "joined_at", label: "Joined At" },
  { value: "left_at", label: "Left At" },
  { value: "created_at", label: "Created At" },
  { value: "updated_at", label: "Updated At" },
  { value: "status", label: "Status" },
  { value: "current_state", label: "Current State" },
]

const MEMBERS_SKELETON_ROWS = 6
const MEMBERS_TABLE_COLUMNS = 8

const SessionDetailSkeleton = () => (
  <div
    className="project-session-detail project-session-detail--skeleton"
    aria-hidden="true"
  >
    <div className="project-session-detail__header">
      <div className="project-session-detail__intro">
        <div className="project-session-detail__skeleton-bar project-session-detail__skeleton-bar--title" />
        <div className="project-session-detail__skeleton-bar project-session-detail__skeleton-bar--meta" />
        <div className="project-session-detail__skeleton-bar project-session-detail__skeleton-bar--meta" />
        <div className="project-session-detail__skeleton-bar project-session-detail__skeleton-bar--note" />
      </div>
      <div className="project-session-detail__timer-slot">
        <div className="project-session-detail__skeleton-bar project-session-detail__skeleton-bar--timer" />
      </div>
      <div className="project-session-detail__actions">
        <div className="project-session-detail__skeleton-bar project-session-detail__skeleton-bar--btn" />
      </div>
    </div>
    <div className="project-session-detail__gallery">
      <div className="project-session-detail__skeleton-bar project-session-detail__skeleton-bar--section" />
      <div className="project-session-detail__gallery-list">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={`gal-skel-${i}`}
            className="project-session-detail__gallery-item"
          >
            <div className="project-session-detail__skeleton-avatar" />
            <div className="project-session-detail__skeleton-bar project-session-detail__skeleton-bar--name" />
          </div>
        ))}
      </div>
    </div>
    <div className="project-session-detail__table-wrap">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={`row-skel-${i}`}
          className="project-session-detail__row project-session-detail__row--skeleton"
        >
          {Array.from({ length: MEMBERS_TABLE_COLUMNS }).map((__, cellIdx) => (
            <div
              key={cellIdx}
              className="project-session-detail__skeleton-cell"
            >
              <div className="project-session-detail__skeleton-bar" />
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
)

/**
 * ProjectSessionDetail
 * ------------------------------------------------------------------
 * Full session detail view (Figma 40006997:89008): header meta, live
 * timer, collaborator gallery, and paginated member activity table.
 */
const ProjectSessionDetail = ({
  session,
  timer,
  isLoading = false,
  error = null,
  members = [],
  gallery = [],
  membersTotal = 0,
  membersPage = 1,
  membersPageSize = 10,
  isMembersLoading = false,
  membersError = null,
  onMembersPageChange,
  searchQuery = "",
  onSearchChange,
  statusFilters = [],
  onStatusFiltersChange,
  sortBy = DEFAULT_SORT_BY,
  onSortByChange,
  sortDirection = DEFAULT_SORT_DIRECTION,
  onSortDirectionChange,
  onExit,
  onTimerToggle,
  isTimerUpdating = false,
  onMinimize,
  onClose,
  alwaysShowClose = false,
  projectId = null,
  project = null,
  cityId = null,
  currentUserId = "",
  existingCollaboratorUserIds = [],
  onAddCollaborators,
  onRemoveCollaborator,
  isMutatingCollaborators = false,
  impressions = {},
  reactingMemberId = null,
  onToggleImpression,
  timerAboveMeta = false,
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [removingMember, setRemovingMember] = useState(null)

  const canRemoveMember = member => {
    if (!onRemoveCollaborator || !member?.userId) return false
    if (session?.hasLeftSession) return false
    const creatorId =
      session?.creatorId || session?.createdBy || session?.hostId || null
    if (
      !currentUserId ||
      !creatorId ||
      String(currentUserId) !== String(creatorId)
    ) {
      return false
    }
    if (String(member.userId) === String(currentUserId)) {
      return false
    }
    // Creator can only remove collaborators who are still invited.
    const statusKey = String(
      member?.statusKey ||
        member?.raw?.status ||
        member?.status ||
        "",
    ).toLowerCase()
    if (statusKey !== "invited" && statusKey !== "pending") {
      return false
    }
    return true
  }

  const handleConfirmRemove = async () => {
    if (!removingMember || isMutatingCollaborators) return
    const ok = await onRemoveCollaborator?.(removingMember)
    if (ok) setRemovingMember(null)
  }

  if (isLoading) {
    return <SessionDetailSkeleton />
  }

  if (error) {
    return (
      <div className="project-session-detail">
        <EmptyState title="Unable to load session" description={error} />
      </div>
    )
  }

  if (!session) {
    return (
      <div className="project-session-detail">
        <EmptyState
          title="Session not found"
          description="This session may have been removed or is no longer available."
        />
      </div>
    )
  }

  const galleryPeople = gallery.length > 0 ? gallery : []
  const displayTimer = timer || session.timer || "00:00:00"
  // After the current user leaves, hide exit / minimize / add-collaborator
  // in the full detail player (session may still be active for others).
  const hasLeftSession = Boolean(session.hasLeftSession)
  const canExit = Boolean(session.canExit) && !hasLeftSession
  const canManageCollaborators =
    Boolean(onAddCollaborators) &&
    session.canManageCollaborators !== false &&
    !hasLeftSession
  const sessionEnded =
    hasLeftSession ||
    String(session.status || "").toLowerCase() === "ended" ||
    Boolean(session.endedAtRaw)
  // Gallery heading: once nobody is still in-session (everyone exited / session over).
  const sessionFullyEnded =
    String(session.status || "").toLowerCase() === "ended" ||
    Boolean(session.endedAtRaw)
  // View-only (non-active project members / admins): show close without
  // changing Exit/Minimize close rules for active members still in session.
  const showCloseForNonActiveMember =
    Boolean(project) && project.is_current_user_active_member !== true
  const showCloseButton =
    Boolean(onClose) &&
    (alwaysShowClose ||
      hasLeftSession ||
      sessionFullyEnded ||
      showCloseForNonActiveMember)
  const impressionsDisabled = showCloseForNonActiveMember
  const everyoneExited =
    sessionFullyEnded ||
    (members.length > 0 &&
      members.every(member => {
        const key = String(
          member?.statusKey || member?.raw?.status || "",
        ).toLowerCase()
        return key === "left" || key === "exited"
      }))
  const galleryTitle = everyoneExited
    ? "Joined Collaborators"
    : "Active Collaborators"

  const timerControl = (
    <SessionTimerControl
      timer={displayTimer}
      timerStatus={session.timerStatus}
      canToggleTimer={!hasLeftSession && session.canToggleTimer}
      showToggle={!sessionEnded}
      isTimerUpdating={isTimerUpdating}
      onTimerToggle={onTimerToggle}
    />
  )

  return (
    <div
      className={`project-session-detail${
        timerAboveMeta ? " project-session-detail--timer-above-meta" : ""
      }`}
    >
      <div className="project-session-detail__header">
        {timerAboveMeta ? (
          <>
            <div className="project-session-detail__intro project-session-detail__intro--tags">
              <SessionDetailMeta
                session={session}
                layout="inline"
                part="tags"
              />
            </div>
            <div className="project-session-detail__timer-row">
              {timerControl}
            </div>
            <div className="project-session-detail__intro project-session-detail__intro--rows">
              <SessionDetailMeta
                session={session}
                layout="inline"
                part="rows"
              />
            </div>
          </>
        ) : (
          <>
            <div className="project-session-detail__intro">
              <SessionDetailMeta session={session} layout="inline" />
            </div>
            <div className="project-session-detail__timer-slot">
              {timerControl}
            </div>
          </>
        )}

        <div className="project-session-detail__actions">
          {canExit ? (
            <CustomButton
              variant="danger"
              size="md"
              type="button"
              trailingIconSrc={ICONS.logout}
              trailingIconAlt=""
              className="project-session-detail__exit-btn"
              onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                onExit?.(event)
              }}
            >
              Exit Session
            </CustomButton>
          ) : null}
          {onMinimize && canExit ? (
            <CustomIconButton
              iconSrc={ICONS.minimize}
              label="Minimize session detail"
              size="md"
              className="project-session-detail__chrome-btn"
              onClick={onMinimize}
            />
          ) : null}
          {showCloseButton ? (
            <CustomIconButton
              iconSrc={ICONS.callRemoveX}
              label="Close session detail"
              size="md"
              className="project-session-detail__chrome-btn"
              onClick={onClose}
            />
          ) : null}
        </div>
      </div>

      <section
        className="project-session-detail__gallery"
        aria-label={galleryTitle}
      >
        <h3 className="project-session-detail__gallery-title">
          {galleryTitle}
        </h3>
        {isMembersLoading ? (
          <div
            className="project-session-detail__gallery-list"
            aria-hidden="true"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={`gal-load-${i}`}
                className="project-session-detail__gallery-item"
              >
                <div className="project-session-detail__skeleton-avatar" />
                <div className="project-session-detail__skeleton-bar project-session-detail__skeleton-bar--name" />
              </div>
            ))}
          </div>
        ) : galleryPeople.length === 0 ? (
          <p className="project-session-detail__empty">
            No collaborators in this session yet.
          </p>
        ) : (
          <div className="project-session-detail__gallery-list">
            {galleryPeople.map(person => (
              <div
                key={person.id || person.name}
                className="project-session-detail__gallery-item"
              >
                <span className="project-session-detail__gallery-avatar">
                  <Avatar src={person.src} name={person.name} size="lg" />
                  <span
                    className={`project-session-detail__gallery-dot project-session-detail__gallery-dot--${
                      person.status || "away"
                    }`}
                    aria-hidden="true"
                  />
                </span>
                <span className="project-session-detail__gallery-name">
                  {person.name?.split(" ")[0] || person.name}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="project-session-detail__members">
        <div className="project-session-detail__controls">
          <h3 className="project-session-detail__members-title">
            All Collaborators ({membersTotal || galleryPeople.length})
          </h3>
          <div className="project-session-detail__controls-actions">
            <SearchInput
              value={searchQuery}
              onChange={e => onSearchChange?.(e.target.value)}
              placeholder="Search by Name..."
              iconSrc={ICONS.search}
              className="project-session-detail__search"
            />
            <FilterDropdown
              label="Filter"
              className="project-session-detail__filter"
              filters={[
                {
                  key: "status",
                  type: "select",
                  label: "Status",
                  multiple: false,
                  value: Array.isArray(statusFilters)
                    ? statusFilters[0] || ""
                    : statusFilters || "",
                  defaultValue: "",
                  onChange: next => {
                    const value = Array.isArray(next)
                      ? String(next[0] || "")
                      : String(next || "")
                    onStatusFiltersChange?.(value)
                  },
                  options: SESSION_COLLAB_STATUS_OPTIONS,
                },
                {
                  key: "sortBy",
                  type: "sortBy",
                  label: "Sort By",
                  value: sortBy,
                  defaultValue: DEFAULT_SORT_BY,
                  onChange: next => onSortByChange?.(next),
                  options: SESSION_COLLAB_SORT_OPTIONS,
                },
                {
                  key: "sortDirection",
                  type: "sortDirection",
                  label: "Order",
                  value: sortDirection,
                  defaultValue: DEFAULT_SORT_DIRECTION,
                  onChange: next => onSortDirectionChange?.(next),
                },
              ]}
            />
            {canManageCollaborators && !sessionEnded ? (
              <CustomButton
                variant="primary"
                size="sm"
                type="button"
                trailingIconSrc={ICONS.plus}
                trailingIconAlt=""
                className="project-session-detail__add-collab"
                onClick={() => setIsAddModalOpen(true)}
                disabled={isMutatingCollaborators}
              >
                Collaborator
              </CustomButton>
            ) : null}
          </div>
        </div>

        <div className="project-session-detail__table-wrap">
          <div
            className="project-session-detail__table"
            role="table"
            aria-label="Session collaborators"
            aria-busy={isMembersLoading || undefined}
          >
            <div className="project-session-detail__head" role="row">
              <div
                className="project-session-detail__cell project-session-detail__cell--name"
                role="columnheader"
              >
                Collaborators
              </div>
              <div
                className="project-session-detail__cell project-session-detail__cell--status"
                role="columnheader"
              >
                Status
              </div>
              <div
                className="project-session-detail__cell project-session-detail__cell--joined"
                role="columnheader"
              >
                Joined at
              </div>
              <div
                className="project-session-detail__cell project-session-detail__cell--note"
                role="columnheader"
              >
                Join Note
              </div>
              <div
                className="project-session-detail__cell project-session-detail__cell--hours"
                role="columnheader"
              >
                Logged Hours
              </div>
              <div
                className="project-session-detail__cell project-session-detail__cell--exit"
                role="columnheader"
              >
                Exit at
              </div>
              <div
                className="project-session-detail__cell project-session-detail__cell--note"
                role="columnheader"
              >
                Exit Note
              </div>
              <div
                className="project-session-detail__cell project-session-detail__cell--impression"
                role="columnheader"
              >
                Impression
              </div>
            </div>

            {isMembersLoading &&
              Array.from({ length: MEMBERS_SKELETON_ROWS }).map((_, i) => (
                <div
                  key={`member-skel-${i}`}
                  className="project-session-detail__row project-session-detail__row--skeleton"
                  role="row"
                  aria-hidden="true"
                >
                  {Array.from({ length: MEMBERS_TABLE_COLUMNS }).map(
                    (__, cellIdx) => (
                      <div
                        key={cellIdx}
                        className="project-session-detail__skeleton-cell"
                      >
                        <div className="project-session-detail__skeleton-bar" />
                      </div>
                    ),
                  )}
                </div>
              ))}

            {!isMembersLoading &&
              members.map(member => {
                const impression = impressions[String(member.id)] || {}
                const likeCount = Number(impression.likeCount || 0)
                const heartCount = Number(impression.heartCount || 0)
                const displayName = nameWithYou(
                  member.name,
                  isSameUserId(member.userId, currentUserId),
                )

                return (
                <div
                  key={member.id}
                  className="project-session-detail__row"
                  role="row"
                >
                  <div
                    className="project-session-detail__cell project-session-detail__cell--name"
                    role="cell"
                  >
                    <span className="project-session-detail__member">
                      <Avatar
                        src={member.avatar}
                        name={member.name}
                        size="sm"
                      />
                      <TruncatedText title={displayName}>
                        {displayName}
                      </TruncatedText>
                    </span>
                  </div>
                  <div
                    className="project-session-detail__cell project-session-detail__cell--status"
                    role="cell"
                  >
                    <span
                      className={`project-session-detail__status project-session-detail__status--${
                        STATUS_CLASS[member.status] || "invited"
                      }`}
                    >
                      {member.status}
                    </span>
                  </div>
                  <div
                    className="project-session-detail__cell project-session-detail__cell--joined"
                    role="cell"
                  >
                    {member.joinedAt}
                  </div>
                  <div
                    className="project-session-detail__cell project-session-detail__cell--note"
                    role="cell"
                  >
                    <span
                      className="project-session-detail__note"
                      title={member.joinNote}
                    >
                      {member.joinNote}
                    </span>
                  </div>
                  <div
                    className="project-session-detail__cell project-session-detail__cell--hours"
                    role="cell"
                  >
                    {member.logged}
                  </div>
                  <div
                    className="project-session-detail__cell project-session-detail__cell--exit"
                    role="cell"
                  >
                    {member.exitAt}
                  </div>
                  <div
                    className="project-session-detail__cell project-session-detail__cell--note"
                    role="cell"
                  >
                    <span
                      className="project-session-detail__note"
                      title={member.exitNote}
                    >
                      {member.exitNote}
                    </span>
                  </div>
                  <div
                    className="project-session-detail__cell project-session-detail__cell--impression"
                    role="cell"
                  >
                    <div className="project-session-detail__impressions">
                      <div className="project-session-detail__impression-action">
                        <CustomIconButton
                          iconSrc={
                            impression.liked
                              ? ICONS.likeFill
                              : ICONS.likeUnfill
                          }
                          label={
                            impression.liked
                              ? `Unlike ${member.name}`
                              : `Like ${member.name}`
                          }
                          size="sm"
                          aria-pressed={Boolean(impression.liked)}
                          disabled={
                            impressionsDisabled ||
                            Boolean(reactingMemberId) ||
                            (currentUserId &&
                              String(member.userId) === String(currentUserId))
                          }
                          onClick={() =>
                            onToggleImpression?.(member.id, "liked")
                          }
                        />
                        {likeCount > 0 ? (
                          <span
                            className="project-session-detail__impression-count"
                            aria-label={`${likeCount} likes`}
                          >
                            {likeCount}
                          </span>
                        ) : null}
                      </div>
                      <div className="project-session-detail__impression-action">
                        <CustomIconButton
                          iconSrc={
                            impression.hearted
                              ? ICONS.heartFill
                              : ICONS.heartOutlineRed
                          }
                          label={
                            impression.hearted
                              ? `Remove heart from ${member.name}`
                              : `Heart ${member.name}`
                          }
                          size="sm"
                          aria-pressed={Boolean(impression.hearted)}
                          disabled={
                            impressionsDisabled ||
                            Boolean(reactingMemberId) ||
                            (currentUserId &&
                              String(member.userId) === String(currentUserId))
                          }
                          onClick={() =>
                            onToggleImpression?.(member.id, "hearted")
                          }
                        />
                        {heartCount > 0 ? (
                          <span
                            className="project-session-detail__impression-count"
                            aria-label={`${heartCount} hearts`}
                          >
                            {heartCount}
                          </span>
                        ) : null}
                      </div>
                      {canRemoveMember(member) && !sessionEnded ? (
                        <CustomIconButton
                          iconSrc={ICONS.trash}
                          label={`Remove ${member.name}`}
                          tone="danger"
                          size="sm"
                          className="project-session-detail__remove-btn"
                          onClick={() => setRemovingMember(member)}
                          disabled={isMutatingCollaborators}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
                )
              })}

            {!isMembersLoading && membersError && (
              <div className="project-session-detail__error">
                {membersError}
              </div>
            )}

            {!isMembersLoading && !membersError && members.length === 0 && (
              <EmptyState
                title="No Collaborators Yet"
                description="Add collaborators to this session and they will appear here."
                actionText={
                  canManageCollaborators && !sessionEnded
                    ? "Add Collaborator"
                    : undefined
                }
                onActionClick={
                  canManageCollaborators && !sessionEnded
                    ? () => setIsAddModalOpen(true)
                    : undefined
                }
              />
            )}
          </div>
        </div>

        <Pagination
          currentPage={membersPage}
          totalItems={membersTotal}
          pageSize={membersPageSize}
          onPageChange={onMembersPageChange}
          itemLabel="Collaborators"
          prevIconSrc={ICONS.chevronLeft}
          nextIconSrc={ICONS.chevronRight}
        />
      </div>

      <AddSessionCollaboratorModal
        isOpen={isAddModalOpen}
        onClose={() => {
          if (!isMutatingCollaborators) setIsAddModalOpen(false)
        }}
        projectId={projectId}
        project={project}
        cityId={cityId}
        existingUserIds={existingCollaboratorUserIds}
        isSubmitting={isMutatingCollaborators}
        onSubmit={onAddCollaborators}
      />

      <DeleteConfirmation
        isOpen={Boolean(removingMember)}
        className="delete-confirmation--above-player"
        onCancel={() => {
          if (!isMutatingCollaborators) setRemovingMember(null)
        }}
        onConfirm={handleConfirmRemove}
        title="Remove Collaborator"
        message={`Are you sure you want to remove ${
          removingMember?.name || "this collaborator"
        } from this session? They will no longer be part of the session.`}
        cancelText="Cancel"
        confirmText="Remove Collaborator"
        confirmLoadingText="Removing..."
        isDeleting={isMutatingCollaborators}
      />
    </div>
  )
}

export default ProjectSessionDetail
